import type { ListAPInvoicesInput } from '@erp/rpc';
import {
  type ColumnConfigEntry,
  OVERDUE_PAYMENT_STATUS_FILTER,
} from '@erp/shared-types';
import { formatDateOnlyLocal } from '@erp/shared-utils';
import { toReceivedAtIsoBounds } from '@/components/accounts-payable/shared/invoice-list-date-filters';
import { selectedExtensionColumns } from '@/components/common/schema-extensions/extensionColumns';
import type { InvoicesSearchParams } from './invoicesSearch';

/** Page size when the URL and the saved preference are both unset. */
export const INVOICES_DEFAULT_PAGE_SIZE = 25;

/** localStorage key shared by the invoices table and its record navigation. */
export const INVOICES_PAGE_SIZE_STORAGE_KEY = 'invoices-page-size';

/**
 * The invoices table's list query. Record navigation must call this with the
 * same page size the table resolved, so both observers share one cache entry.
 * `pageSize` is an argument because it can come from localStorage rather than
 * the URL.
 */
export function buildInvoiceListInput(
  search: InvoicesSearchParams,
  pageSize: number,
  now: Date = new Date(),
): ListAPInvoicesInput {
  const limit = pageSize > 0 ? pageSize : INVOICES_DEFAULT_PAGE_SIZE;
  const page = search.page && search.page > 0 ? search.page : 1;
  return {
    invoiceNumber: search.invoiceNumber || undefined,
    supplierIds: nonEmpty(search.supplierIds),
    status: nonEmpty(search.status),
    paymentStatus: nonEmpty(search.paymentStatus),
    trouble: search.trouble,
    // Overdue means past due on the viewer's calendar, matching the badge.
    overdueAsOf: search.paymentStatus?.includes(OVERDUE_PAYMENT_STATUS_FILTER)
      ? formatDateOnlyLocal(now)
      : undefined,
    invoiceDateFrom: search.invoiceDateFrom,
    invoiceDateTo: search.invoiceDateTo,
    dueDateFrom: search.dueDateFrom,
    dueDateTo: search.dueDateTo,
    ...toReceivedAtIsoBounds({
      from: search.receivedDateFrom,
      to: search.receivedDateTo,
    }),
    sortBy: search.sortBy,
    sortDirection: search.sortDirection,
    invoiceIds: nonEmpty(search.invoiceIds),
    excludeSnoozed: search.showSnoozed !== true,
    limit,
    offset: (page - 1) * limit,
  };
}

/**
 * The list query with the extension values the resolved columns display. The
 * table and record navigation both call this so their reads share a cache
 * entry. No visible extension column leaves the request unchanged.
 */
export function buildInvoiceListInputWithColumns(
  search: InvoicesSearchParams,
  pageSize: number,
  columns: ColumnConfigEntry[],
): ListAPInvoicesInput {
  const input = buildInvoiceListInput(search, pageSize);
  const extensionColumns = selectedExtensionColumns(columns);
  return extensionColumns.length > 0 ? { ...input, extensionColumns } : input;
}

function nonEmpty(values: string[] | undefined): string[] | undefined {
  return values && values.length > 0 ? values : undefined;
}

/** Search to keep when opening another invoice in the same filtered list. */
export function invoiceNavigationSearch(
  current: InvoicesSearchParams,
  page: number,
): InvoicesSearchParams {
  return {
    ...current,
    page: page > 1 ? page : undefined,
  };
}
